import capitalize from 'lodash/capitalize';
import words from 'lodash/words';

const prefix = 'Ds';

const normalizeElementName = (name: string): string =>
  words(name).map(capitalize).join('');

const normalizeAttributeName = (name: string): string =>
  words(name)
    .map((part, index) => (index === 0 ? part.toLowerCase() : capitalize(part)))
    .join('');

export const globalAttribute = (attributeName: string): string =>
  `${prefix}-${normalizeAttributeName(attributeName)}`;

export const globalClassNames = {
  disabled: globalAttribute('disabled'),
  readOnly: globalAttribute('readOnly'),
  error: globalAttribute('error'),
  success: globalAttribute('success'),
};

type ClassNamesFactory = {
  root: () => string;
  element: (elementName: string) => string;
  attribute: (attributeName: string) => string;
  globalAttribute: (attributeName: string) => string;
  globalClassNames: typeof globalClassNames;
  toAttrClasses: <POSSIBLE_VALUE extends string>(
    attributeName: string,
    possibleValues: ReadonlyArray<POSSIBLE_VALUE>
  ) => Record<POSSIBLE_VALUE, string>;
};

export const classNamesFor = (component: string): ClassNamesFactory => {
  const root = `${prefix}-${normalizeElementName(component)}`;

  const result: ClassNamesFactory = {
    /**
     * Class, for the outermost element of the component
     */
    root: () => root,
    /**
     * Class, for an element with a semantic meaning within the component
     * E.g. DS-Table-HeaderRow - `DS` is prefix, `Table` is component, `HeaderRow` is element
     */
    element: (elementName) => `${root}-${normalizeElementName(elementName)}`,
    /**
     * Class, used to describe an invariant or attribute of the specific component or its element
     * E.g. Divider can be horizontal or vertical, so `attribute` would be the right class type to describe,
     *      so `<Divider orientation="vertical" />` would have class `Ds-Divider-vertical`
     */
    attribute: (attributeName) =>
      `${root}-${normalizeAttributeName(attributeName)}`,
    /**
     * Class, used to describe the generic attribute, common to many components
     * E.g. Many components can be disabled
     *      so `<Button disabled />` would have class `Ds-disabled`
     */
    globalAttribute,
    /**
     * Pre-defined global class names
     */
    globalClassNames,
    /**
     * Accepts attribute name and array of possible values.
     * Returns an object where each key is a possible value, and the value is a generated class name.
     * Use for known sets of possible values - sizes, variants, colors, etc.
     * @example
     * const classNames = classNamesFor('Avatar');
     * const sizes = classNames.toAttrClasses('size', ['sm', 'md', 'lg'] as const);
     * // sizes = { sm: 'Ds-Avatar-sizeSm', md: 'Ds-Avatar-sizeMd', lg: 'Ds-Avatar-sizeLg' }
     */
    toAttrClasses: <POSSIBLE_VALUE extends string>(
      attrName: string,
      possibleValues: ReadonlyArray<POSSIBLE_VALUE>
    ) => {
      return possibleValues.reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: result.attribute(`${attrName}${capitalize(curr)}`),
        }),
        {} as Record<POSSIBLE_VALUE, string>
      );
    },
  };
  return result;
};
